import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import DataSourceModal from "@/components/DataSourceModal";
import { cn } from "@/lib/utils";
import { Upload, FileSpreadsheet, Check, X, ArrowRight, ArrowLeft, Loader2, RefreshCw, Download } from "lucide-react";
import { toast } from "sonner";

const SAMPLE_FILES = [
  { type: "PO", url: "/po_mock.csv", filename: "po_mock.csv" },
  { type: "GRN", url: "/grn_mock.csv", filename: "grn_mock.csv" },
  { type: "Invoice", url: "/invoice_mock.csv", filename: "invoice_mock.csv" }
];

const DEFAULT_IMPORTED_COUNT = 1284;
const UPLOAD_HIGHLIGHT_DURATION_MS = 2200;

const FILE_TYPES = [
  { 
    type: "PO", 
    label: "Purchase Orders",
    description: "Required: po_number, vendor_id, amount, date",
    color: "bg-blue-50 border-blue-200"
  },
  { 
    type: "GRN", 
    label: "Goods Receipt Notes",
    description: "Required: grn_number, po_number, received_qty, date",
    color: "bg-emerald-50 border-emerald-200"
  },
  { 
    type: "Invoice", 
    label: "Invoices",
    description: "Required: invoice_number, po_number, amount, date",
    color: "bg-amber-50 border-amber-200"
  }
];

const FileUploadCard = ({ fileConfig, upload, onUpload, loading }) => {
  const fileInputRef = useRef(null);
  const hasUpload = !!upload;
  const isValid = upload?.schema_valid;
  
  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error("Please upload a CSV file");
      return;
    }
    
    await onUpload(fileConfig.type, file);
    e.target.value = "";
  };

  return (
    <div
      className={`file-upload-card ${hasUpload ? (isValid ? "valid" : "invalid") : ""}`}
      onClick={handleClick}
      data-testid={`upload-card-${fileConfig.type.toLowerCase()}`}
    >
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept=".csv"
        className="hidden"
        data-testid={`file-input-${fileConfig.type.toLowerCase()}`}
      />
      
      <div className="flex flex-col items-center gap-3">
        {loading ? (
          <Loader2 size={32} className="text-slate-400 animate-spin" />
        ) : hasUpload ? (
          isValid ? (
            <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
              <Check size={24} className="text-emerald-600" />
            </div>
          ) : (
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
              <X size={24} className="text-red-600" />
            </div>
          )
        ) : (
          <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center">
            <FileSpreadsheet size={24} className="text-slate-400" />
          </div>
        )}
        
        <div>
          <h3 className="font-semibold text-[#062B55]">{fileConfig.label}</h3>
          <p className="text-xs text-slate-500 mt-1">{fileConfig.description}</p>
        </div>
        
        {hasUpload ? (
          <div className="mt-2 text-sm">
            <p className="font-mono text-slate-600">{upload.file_name}</p>
            <p className="text-slate-500">{upload.row_count} rows</p>
            {!isValid && (
              <p className="text-red-500 text-xs mt-1" data-testid={`error-${fileConfig.type.toLowerCase()}`}>
                {upload.blocking_error}
              </p>
            )}
            <button
              className="text-[#6FA8F7] hover:text-[#062B55] text-xs font-medium mt-2 flex items-center gap-1 mx-auto"
              onClick={(e) => {
                e.stopPropagation();
                handleClick();
              }}
              data-testid={`replace-${fileConfig.type.toLowerCase()}`}
            >
              <RefreshCw size={12} />
              Replace file
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-sm text-[#6FA8F7]">
            <Upload size={16} />
            <span>Click to upload CSV</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default function UploadDataScreen({ runId, uploads, onUpload, onContinue, onBack, loading }) {
  const navigate = useNavigate();
  const uploadGridRef = useRef(null);
  const highlightTimeoutRef = useRef(null);
  const [highlightUploadArea, setHighlightUploadArea] = useState(false);
  const [revealSource, setRevealSource] = useState(null);
  const [selectedSource, setSelectedSource] = useState(() => {
    const sampleFileNames = new Set(SAMPLE_FILES.map((sampleFile) => sampleFile.filename));
    const isSapSeededRun =
      uploads.length === SAMPLE_FILES.length &&
      uploads.every((upload) => sampleFileNames.has(upload.file_name));

    if (isSapSeededRun) {
      return "sap";
    }

    return uploads.length > 0 ? "manual" : null;
  });

  useEffect(() => {
    if (!runId) {
      navigate("/");
    }
  }, [runId, navigate]);

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) {
        window.clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, []);

  const getUploadForType = (type) => {
    return uploads.find(u => u.file_type === type);
  };

  const revealUploadArea = (source = "manual") => {
    if (highlightTimeoutRef.current) {
      window.clearTimeout(highlightTimeoutRef.current);
    }

    setSelectedSource(source);
    setRevealSource(source);
    setHighlightUploadArea(true);
    uploadGridRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

    highlightTimeoutRef.current = window.setTimeout(() => {
      setHighlightUploadArea(false);
      setRevealSource(null);
    }, UPLOAD_HIGHLIGHT_DURATION_MS);
  };

  const handleSapImport = async () => {
    let importedCount = 0;

    for (const sampleFile of SAMPLE_FILES) {
      const response = await fetch(sampleFile.url);

      if (!response.ok) {
        throw new Error(`Unable to download ${sampleFile.filename}`);
      }

      const blob = await response.blob();
      const file = new File([blob], sampleFile.filename, {
        type: blob.type || "text/csv",
      });

      const upload = await onUpload(sampleFile.type, file);
      importedCount += upload?.row_count ?? 0;
    }

    return { importedCount: importedCount || DEFAULT_IMPORTED_COUNT };
  };

  const allFilesUploaded = FILE_TYPES.every(ft => getUploadForType(ft.type));
  const canContinue = allFilesUploaded;
  const triggerLabel =
    selectedSource === "manual"
      ? "Switch to ERP"
      : selectedSource === "sap"
        ? "Switch to Manual Upload"
        : "Select Data Source";
  const preferredTab = selectedSource === "manual" ? "integrations" : "manual";
  const shouldAutoOpenSelector = selectedSource === null && uploads.length === 0;

  return (
    <div className="max-w-4xl mx-auto animate-fadeIn">
      {/* Header */}
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight text-[#062B55]" data-testid="upload-headline">
            Upload PO, GRN, and Invoice files
          </h1>
          <p className="text-slate-600 mt-2">
            Upload all three CSV exports for this scope.
            <br />
            <span className="text-slate-500">We&apos;ll check for required columns.</span>
          </p>
        </div>

        <div className="sm:pt-1">
          <DataSourceModal
            defaultOpen={shouldAutoOpenSelector}
            preferredTab={preferredTab}
            triggerLabel={triggerLabel}
            autoCloseOnSapSuccess
            onRevealUploadArea={revealUploadArea}
            onSapImport={handleSapImport}
          />
        </div>
      </div>

      {/* Sample Files Download */}
      <div className="flex items-center gap-3 mb-6 px-4 py-3 bg-slate-50/80 border border-slate-100 rounded-xl" data-testid="sample-files-bar">
        <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-400 whitespace-nowrap">Sample files</span>
        <div className="flex items-center gap-2 flex-wrap">
          {SAMPLE_FILES.map(sf => (
            <a
              key={sf.type}
              href={sf.url}
              download={sf.filename}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-[#062B55] hover:text-[#6FA8F7] bg-white border border-slate-200 rounded-lg px-3 py-1.5 transition-colors hover:border-[#6FA8F7]/40"
              data-testid={`download-sample-${sf.type.toLowerCase()}`}
            >
              <Download size={12} />
              {sf.filename}
            </a>
          ))}
        </div>
      </div>

      {/* Upload Cards Grid */}
      <div
        ref={uploadGridRef}
        className={cn(
          "mb-8 rounded-[28px] p-2 transition-all duration-500",
          highlightUploadArea
            ? "bg-[#6FA8F7]/10 ring-2 ring-[#6FA8F7]/40 shadow-[0_20px_45px_-30px_rgba(111,168,247,0.7)]"
            : ""
        )}
      >
        {highlightUploadArea && (
          <div className="mb-4 rounded-2xl border border-[#6FA8F7]/30 bg-white/90 px-4 py-3 text-sm text-slate-600 shadow-sm">
            {revealSource === "sap"
              ? "SAP sync complete. Your three source files are loaded below."
              : "Manual upload selected. Choose a file type below to continue importing your audit data."}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {FILE_TYPES.map(fileConfig => (
            <FileUploadCard
              key={fileConfig.type}
              fileConfig={fileConfig}
              upload={getUploadForType(fileConfig.type)}
              onUpload={onUpload}
              loading={loading}
            />
          ))}
        </div>
      </div>

      {/* Status Summary */}
      <Card className="card-surface mb-8">
        <CardContent className="p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {FILE_TYPES.map(ft => {
                const upload = getUploadForType(ft.type);
                return (
                  <div key={ft.type} className="flex items-center gap-2">
                    {upload ? (
                      upload.schema_valid ? (
                        <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center">
                          <Check size={12} className="text-emerald-600" />
                        </div>
                      ) : (
                        <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center">
                          <X size={12} className="text-red-500" />
                        </div>
                      )
                    ) : (
                      <div className="w-5 h-5 rounded-full border-2 border-slate-200 bg-slate-50" />
                    )}
                    <span className={`text-sm ${upload?.schema_valid ? "text-emerald-600 font-medium" : "text-slate-500"}`}>
                      {ft.type}
                    </span>
                  </div>
                );
              })}
            </div>
            <span className="text-sm text-slate-500">
              {uploads.filter(u => u.schema_valid).length}/3 files valid
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Empty State */}
      {uploads.length === 0 && (
        <div className="empty-state">
          <div className="empty-state-icon">
            <Upload size={28} />
          </div>
          <p className="empty-state-title">Upload PO, GRN, and Invoice to proceed</p>
          <p className="empty-state-text">Click on any card above to upload your CSV files</p>
        </div>
      )}

      {/* Error Message */}
      {uploads.length > 0 && !canContinue && (
        <p className="text-sm text-red-500 mb-4" data-testid="upload-error">
          Upload all three files to continue.
        </p>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between pt-6">
        <Button
          variant="ghost"
          onClick={onBack}
          className="flex items-center gap-2 text-slate-600 rounded-xl"
          data-testid="upload-back-button"
        >
          <ArrowLeft size={18} />
          Back
        </Button>
        <Button
          onClick={onContinue}
          disabled={!canContinue || loading}
          className="btn-primary flex items-center gap-2"
          data-testid="upload-continue-button"
        >
          Continue
          <ArrowRight size={18} />
        </Button>
      </div>
    </div>
  );
}
